"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.lifecycleRoles = void 0;
exports.deriveFlowLifecycle = deriveFlowLifecycle;
/** Structural application lifecycle. A graph leaf is never assumed to delete an account. */
const app_flow_types_1 = require("./app-flow-types");
exports.lifecycleRoles = app_flow_types_1.FLOW_LIFECYCLE_ROLES;
function deriveFlowLifecycle(catalogue) {
    const nodes = new Map(), occurrences = new Map(), edges = [];
    for (const flow of catalogue.flows)
        for (const step of flow.steps) {
            const key = step.stateId
                ? `state:${step.stateId}`
                : `flow:${flow.id}:${step.id}`;
            occurrences.set(`${flow.id}:${step.id}`, key);
            let node = nodes.get(key);
            if (!node) {
                node = {
                    id: key,
                    roles: new Set(),
                    occurrences: [],
                    next: new Set(),
                    previous: new Set(),
                };
                nodes.set(key, node);
            }
            if (step.lifecycle?.role)
                node.roles.add(step.lifecycle.role);
            node.occurrences.push({
                flowId: flow.id,
                stepId: step.id,
                title: step.title,
                planned: step.implementation?.ui === 'planned',
            });
        }
    const connect = (from, to, label, kind) => {
        const source = from === undefined ? undefined : nodes.get(from);
        const target = to === undefined ? undefined : nodes.get(to);
        if (!from || !to || !source || !target)
            return;
        source.next.add(to);
        target.previous.add(from);
        edges.push({ from, to, label, kind });
    };
    for (const flow of catalogue.flows)
        for (const edge of flow.transitions)
            connect(occurrences.get(`${flow.id}:${edge.from}`), occurrences.get(`${flow.id}:${edge.to}`), edge.label, 'within-flow');
    for (const edge of catalogue.connections ?? [])
        connect(occurrences.get(`${edge.from.flowId}:${edge.from.stepId}`), occurrences.get(`${edge.to.flowId}:${edge.to.stepId}`), edge.label, 'between-flows');
    for (const edge of catalogue.navigation?.edges ?? [])
        connect(occurrences.get(`${edge.from.flowId}:${edge.from.stepId}`), occurrences.get(`${edge.to.flowId}:${edge.to.stepId}`), edge.label, 'contract');
    // Condense loops first; sorting a cyclic graph directly would lose screens.
    let cursor = 0;
    const indices = new Map(), low = new Map(), stack = [], stacked = new Set(), components = [];
    const strong = (key) => {
        indices.set(key, cursor);
        low.set(key, cursor++);
        stack.push(key);
        stacked.add(key);
        for (const next of [...(nodes.get(key)?.next ?? [])].sort()) {
            if (!indices.has(next)) {
                strong(next);
                low.set(key, Math.min(low.get(key), low.get(next)));
            }
            else if (stacked.has(next))
                low.set(key, Math.min(low.get(key), indices.get(next)));
        }
        if (low.get(key) === indices.get(key)) {
            const group = [];
            let item;
            do {
                item = stack.pop();
                stacked.delete(item);
                group.push(item);
            } while (item !== key);
            components.push(group.sort());
        }
    };
    for (const key of [...nodes.keys()].sort())
        if (!indices.has(key))
            strong(key);
    const componentOf = new Map(components.flatMap((group, index) => group.map((key) => [key, index])));
    const nextGroups = components.map(() => new Set()), incoming = components.map(() => 0);
    for (const { from, to } of edges) {
        const a = componentOf.get(from), b = componentOf.get(to);
        if (a !== b && !nextGroups[a].has(b)) {
            nextGroups[a].add(b);
            incoming[b]++;
        }
    }
    const priority = (roles) => roles.has('app-start')
        ? 0
        : roles.has('account-create')
            ? 1
            : roles.has('sign-in')
                ? 2
                : roles.has('account-delete')
                    ? 6
                    : roles.has('sign-out')
                        ? 5
                        : roles.has('external-exit')
                            ? 4
                            : 3;
    const groupPriority = (index) => Math.min(...components[index].map(key => priority(nodes.get(key).roles)));
    const queue = incoming.flatMap((n, index) => (n ? [] : [index])), orderedNodes = [];
    while (queue.length) {
        queue.sort((a, b) => groupPriority(a) - groupPriority(b) ||
            components[a][0].localeCompare(components[b][0]));
        const index = queue.shift();
        orderedNodes.push(...components[index]);
        for (const next of nextGroups[index])
            if (--incoming[next] === 0)
                queue.push(next);
    }
    const rank = new Map(orderedNodes.map((key, index) => [key, index]));
    const entries = [...nodes.values()].filter(node => node.roles.has('app-start'));
    const reachable = new Set(entries.map(node => node.id)), visitQueue = [...reachable];
    for (const id of visitQueue)
        for (const next of nodes.get(id).next)
            if (!reachable.has(next)) {
                reachable.add(next);
                visitQueue.push(next);
            }
    const flowInfo = {};
    for (const flow of catalogue.flows) {
        const roles = new Set(flow.steps.flatMap(step => step.lifecycle?.role ? [step.lifecycle.role] : []));
        const startNode = occurrences.get(`${flow.id}:${flow.start}`);
        const visited = new Set(), stepOrder = [];
        const walk = (id) => {
            if (visited.has(id))
                return;
            visited.add(id);
            stepOrder.push(id);
            for (const edge of flow.transitions
                .filter(edge => edge.from === id)
                .sort((a, b) => (rank.get(occurrences.get(`${flow.id}:${a.to}`)) ?? 0) -
                (rank.get(occurrences.get(`${flow.id}:${b.to}`)) ?? 0) ||
                (a.id ?? '').localeCompare(b.id ?? '')))
                walk(edge.to);
        };
        if (flow.kind === 'catalogue')
            stepOrder.push(...flow.steps.map(step => step.id));
        else {
            walk(flow.start);
            for (const step of flow.steps)
                walk(step.id);
        }
        const phase = roles.has('account-delete')
            ? 6
            : roles.has('sign-out')
                ? 5
                : roles.has('external-exit')
                    ? 4
                    : roles.has('app-start')
                        ? roles.has('account-create')
                            ? 1
                            : 0
                        : flow.kind === 'catalogue'
                            ? 3
                            : 2;
        flowInfo[flow.id] = {
            phase,
            roles: [...roles],
            stepOrder,
            rank: (startNode === undefined ? undefined : rank.get(startNode)) ?? 0,
            reachable: startNode !== undefined && reachable.has(startNode),
            sequence: flow.kind === 'catalogue' ? 'state-catalogue' : 'connected-steps',
        };
    }
    const flowOrder = catalogue.flows
        .map(flow => flow.id)
        .sort((a, b) => flowInfo[a].phase - flowInfo[b].phase ||
        flowInfo[a].sequence.localeCompare(flowInfo[b].sequence) ||
        Number(flowInfo[b].reachable) - Number(flowInfo[a].reachable) ||
        flowInfo[a].rank - flowInfo[b].rank ||
        a.localeCompare(b));
    const boundaries = exports.lifecycleRoles.map(role => ({
        role,
        screens: [...nodes.values()]
            .filter(node => node.roles.has(role))
            .flatMap(node => node.occurrences)
            .sort((a, b) => flowOrder.indexOf(a.flowId) - flowOrder.indexOf(b.flowId) ||
            a.stepId.localeCompare(b.stepId)),
    }));
    return {
        flowOrder,
        flows: flowInfo,
        boundaries,
        edges,
        cycles: components.filter(group => group.length > 1 || nodes.get(group[0]).next.has(group[0])),
        unconnectedRoots: [...nodes.values()]
            .filter(node => !node.previous.size && !node.roles.has('app-start'))
            .map(node => node.id),
        terminalCandidates: [...nodes.values()]
            .filter(node => !node.next.size &&
            ![...node.roles].some(role => ['sign-out', 'account-delete', 'external-exit'].includes(role)))
            .map(node => node.id),
    };
}
